import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileSpreadsheet, ListChecks, Loader2 } from 'lucide-react';
import { Button, CustomSelect } from '../../components/shared';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import DatePicker from '../../components/client/DatePicker';
import { marketplaceTaxReportApi } from '../../services/marketplaceTaxReportService';
import { useAdminMarkets } from '../../services/marketService';
import { toMarketSelectOptions } from '../../utils/marketOptions';
import { useToastContext } from '../../contexts/ToastContext';

const REPORT_OPTIONS = [
  { value: 'cra', label: 'CRA platform seller report' },
  { value: '1099k', label: 'US 1099-K prep' },
  { value: 'dac7', label: 'DAC7 completeness' },
  { value: 'remittance', label: 'Tax remittance' },
];

const yearOptions = () => {
  const y = new Date().getFullYear();
  return [0, 1, 2, 3, 4].map((i) => {
    const year = String(y - i);
    return { value: year, label: year };
  });
};

const AdminMarketplaceTaxReports = () => {
  const { success, error: showError } = useToastContext();
  const [reportType, setReportType] = useState('cra');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [country, setCountry] = useState('');
  const [downloading, setDownloading] = useState(false);

  const { data: marketsData } = useAdminMarkets();
  const marketFilterOptions = useMemo(
    () => [{ value: 'ALL', label: 'All markets' }, ...toMarketSelectOptions(marketsData?.data || [])],
    [marketsData]
  );
  const isRemittance = reportType === 'remittance';
  const isYearBound = reportType !== 'dac7' && !isRemittance;

  const { data, isLoading } = useQuery({
    queryKey: ['admin-marketplace-tax-reports'],
    queryFn: () => marketplaceTaxReportApi.list(),
    staleTime: 60_000,
  });

  const previewParams = useMemo(() => {
    if (isRemittance) {
      return {
        type: 'remittance',
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        country: country.trim() ? country.trim().toUpperCase() : undefined,
      };
    }
    return {
      type: reportType,
      year: isYearBound ? Number(year) : undefined,
    };
  }, [reportType, year, dateFrom, dateTo, country, isRemittance, isYearBound]);

  const previewQuery = useQuery({
    queryKey: ['admin-marketplace-tax-preview', previewParams],
    queryFn: () => marketplaceTaxReportApi.preview(previewParams),
    staleTime: 30_000,
  });

  const years = useMemo(() => yearOptions(), []);
  const catalog = data?.data?.reports || [];
  const preview = data?.data?.preview || {};
  const rows = previewQuery.data?.data?.rows || [];
  const remittanceSummary = previewQuery.data?.data?.summary || null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await marketplaceTaxReportApi.exportCsv(previewParams);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = isRemittance
        ? `marketplace-remittance-${new Date().toISOString().slice(0, 10)}.csv`
        : `marketplace-${reportType}-${reportType === 'dac7' ? 'completeness' : year}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      success('Export downloaded');
    } catch (err) {
      showError(err?.response?.data?.message || err?.message || 'Export failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Marketplace tax reports"
        subtitle="CRA, 1099-K prep, DAC7 completeness, and tax remittance — from ledger + tax lines."
      />

      <AdminGuideCallout
        title="How to use"
        steps={[
          'Pick a report type. Seller reports use tax year; remittance uses an optional date range and country.',
          'Preview rows below, then download CSV for counsel / filing prep.',
          'Remittance net owing = open tax lines still liable (refunds are reversed).',
        ]}
        icon={ListChecks}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ['CRA sellers (YTD)', preview.cra_sellers ?? '—'],
          ['1099-K sellers (YTD)', preview.form_1099k_sellers ?? '—'],
          ['DAC7 profiles', preview.dac7_profiles ?? '—'],
          ['Remittance owing', preview.remittance_net_owing ?? '—'],
        ].map(([label, value]) => (
          <div
            key={label}
            className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3"
          >
            <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
            <p className="text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-50 mt-1">{value}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl">
          <CustomSelect
            label="Report type"
            value={reportType}
            onChange={setReportType}
            options={REPORT_OPTIONS}
            size="sm"
          />
          {isYearBound && (
            <CustomSelect
              label="Tax year"
              value={year}
              onChange={setYear}
              options={years}
              size="sm"
            />
          )}
          {isRemittance && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  From
                </label>
                <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="From" size="sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  To
                </label>
                <DatePicker value={dateTo} onChange={setDateTo} placeholder="To" size="sm" />
              </div>
              <CustomSelect
                label="Market (optional)"
                value={country || 'ALL'}
                onChange={(value) => setCountry(value === 'ALL' ? '' : value)}
                options={marketFilterOptions}
                placeholder="All markets"
                size="sm"
              />
            </>
          )}
        </div>

        {isRemittance && remittanceSummary && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Open {remittanceSummary.tax_open} · Reversed {remittanceSummary.tax_reversed} · Net owing{' '}
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {remittanceSummary.net_owing}
            </span>{' '}
            across {remittanceSummary.jurisdiction_count} jurisdiction
            {remittanceSummary.jurisdiction_count === 1 ? '' : 's'}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Button className="!rounded-xl" onClick={handleDownload} disabled={downloading || isLoading}>
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download CSV
          </Button>
        </div>

        <div className="rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-950/60 flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <FileSpreadsheet className="w-4 h-4" />
            Preview ({rows.length} row{rows.length === 1 ? '' : 's'})
          </div>
          {previewQuery.isLoading ? (
            <div className="py-12 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary-600" />
            </div>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">
              {isRemittance
                ? 'No tax lines yet. After checkout collects tax, open liabilities appear here.'
                : 'No seller rows yet for this selection. After vendors book in an active market, totals appear here.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-950/40 text-left text-gray-500">
                  <tr>
                    {Object.keys(rows[0]).slice(0, 8).map((h) => (
                      <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 25).map((row, idx) => (
                    <tr key={idx} className="border-t border-gray-100 dark:border-gray-800">
                      {Object.keys(rows[0]).slice(0, 8).map((h) => (
                        <td key={h} className="px-3 py-2 text-gray-800 dark:text-gray-200 whitespace-nowrap">
                          {row[h] == null || row[h] === '' ? '—' : String(row[h])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {catalog.length > 0 && (
          <ul className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
            {catalog.map((r) => (
              <li key={r.key}>
                <span className="font-medium text-gray-700 dark:text-gray-300">{r.label}:</span>{' '}
                {r.description}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminMarketplaceTaxReports;
